import { InlineWidget } from 'react-calendly';
import { useEffect, useState } from 'react';
import { buttonStyles, pageStyles } from '@/styles/sharedStyles';

interface CalendlyBookingProps {
  onBookingComplete: (bookingData: any) => void;
  stepData?: any;
  flowType?: 'one-off' | 'subscription';
}

export function CalendlyBooking({ onBookingComplete, stepData, flowType }: CalendlyBookingProps) {
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const isPlaceholderUrl = calendlyUrl.includes('your-username');

  // Fetch Calendly URL from server
  useEffect(() => {
    fetch('/api/calendly/url')
      .then(response => response.json())
      .then(data => {
        if (data.success && data.url) {
          setCalendlyUrl(data.url);
        }
      })
      .catch(error => {
        console.error('Failed to fetch Calendly URL:', error);
        // Keep default URL on error
      });
  }, []);

  useEffect(() => {
    const handleCalendlyEvent = (event: MessageEvent) => {
      if (event.origin === 'https://calendly.com' && event.data?.event) {
        const eventData = event.data;

        if (eventData.event === 'calendly.event_scheduled') {
          const bookingData = {
            date: eventData.payload?.scheduled_event?.start_time || new Date().toISOString(),
            eventType: eventData.payload?.scheduled_event?.event_type?.name || 'Consultation',
            meetingUrl: eventData.payload?.scheduled_event?.location?.join_url || '',
            inviteeData: eventData.payload?.invitee || {},
          };

          onBookingComplete(bookingData);
        }
      }
    };

    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [onBookingComplete]);

  return (
    <div className="p-2 sm:p-4">
      <div className="w-full max-w-none mx-auto bg-white rounded-lg sm:rounded-3xl shadow-lg overflow-hidden lg:min-w-[1200px]">
        <div className="flex flex-col lg:flex-row">
          {/* Left Section - Header on mobile/tablet, sidebar on desktop */}
          <div className="w-full md:w-80 p-6 sm:p-8 lg:p-8 xl:p-12 border-b lg:border-b-0 lg:border-r border-gray-200 flex-shrink-0">
            <div className="space-y-4 sm:space-y-6 lg:space-y-6 xl:space-y-8">
              <div>
                <h2 className="text-xl sm:text-2xl font-medium text-gray-800 mb-4 sm:mb-6 lg:mb-6 xl:mb-8">
                  Birdie Gifts
                </h2>

                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6 lg:mb-4 xl:mb-6">
                  30 Minute Meeting
                </h1>

                <div className="flex items-center gap-3 text-gray-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base sm:text-lg">30 min</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Calendly Widget */}
          <div className="flex-1 min-h-0">
            <div 
              className="h-[500px] sm:h-[600px] lg:h-[calc(100vh-10px)]"
              style={{ minHeight: '500px' }}
            >
              {isPlaceholderUrl ? (
                <div className="flex items-center justify-center h-full px-4">
                  <div className="text-center space-y-4 p-4 sm:p-8 max-w-md">
                    <div className="text-4xl sm:text-6xl">📅</div>
                    <h3 className={`${pageStyles.title} text-lg sm:text-xl lg:text-2xl`}>
                      Calendly Integration Setup Required
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      To enable booking functionality, please update the Calendly URL in the CalendlyBooking component
                      with your actual Calendly booking link.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() =>
                          onBookingComplete({
                            date: new Date().toISOString(),
                            eventType: 'Demo Consultation',
                            meetingUrl: 'https://demo-meeting-url.com',
                            inviteeData: { email: stepData?.step2?.email || 'demo@example.com' },
                          })
                        }
                        className={`${buttonStyles.primary} text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3`}
                      >
                        Continue with Demo Booking
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <InlineWidget
                  url={calendlyUrl}
                  styles={{
                    height: '100%',
                    width: '100%',
                  }}
                  pageSettings={{
                    backgroundColor: 'ffffff',
                    hideEventTypeDetails: true,
                    hideLandingPageDetails: true,
                    primaryColor: '7F9B3C',
                    textColor: '000000',
                  }}
                  prefill={{
                    email: stepData?.step2?.email || '',
                    firstName: stepData?.step2?.firstName || '',
                    lastName: stepData?.step2?.lastName || '',
                  }}
                  utm={{
                    utmCampaign: 'birdie-gift-booking',
                    utmSource: 'birdie-app',
                    utmMedium: flowType || 'web',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}